<template>
    <AonModal
        class="patent-list"
        :class="[{ inline: props.inline }, { absolute: props.absolute }]"
        message=""
        col-width="10"
        @close="closePatentList"
    >
        <div class="modal-content d-flex flex-column">
            <div class="patent-list-header d-flex align-items-center">
                <div class="source-display py-4 px-4">
                    <div class="basics-wrap d-flex align-items-center">
                        <p v-if="showLitigationTitle" class="inter-font semi-bold grey01--text">
                            Litigation for:
                        </p>
                        <p v-if="showCountry" class="inter-font semi-bold grey01--text mr-1">
                            {{ patentViewerStore.ipBasicsObject?.country }} |
                        </p>
                        <p v-if="showBasicsInfo" class="inter-font semi-bold grey01--text">
                            {{ publicationType }}{{ claimBreadthText }}{{ validityText }}
                        </p>
                    </div>
                    <h4 class="inter-font semi-bold grey01--text mt-1 mb-n4">
                        {{ entityTitle }}
                    </h4>
                </div>
            </div>

            <div class="table-holder d-flex flex-column flex-grow-1 h-full pa-4">
                <div
                    class="actions-parent d-flex align-items-center justify-content-between w-full mb-3"
                >
                    <template v-if="showQuickFilters">
                        <MoatDropdown
                            class="mr-4"
                            v-model="selectedQuickFilter"
                            :options="quickFilterDropdownOptions"
                            placeholder="Preset Filters"
                            @change="selectQuickFilter"
                        />
                    </template>
                    <div
                        v-if="props.allowPruning"
                        class="actions-parent d-flex align-items-center justify-content-between w-full"
                        style="min-height: 30px; height: 30px"
                    >
                        <div class="left d-flex align-items-center">
                            <div class="total-selected mr-10 ml-1" v-if="selectedRows.length > 0">
                                <p class="geist-font">{{ selectedRows.length }} selected</p>
                            </div>
                            <div
                                class="bulk-actions d-flex align-items-center"
                                v-if="selectedRows.length > 0"
                            >
                                <div
                                    class="bulk-action d-flex align-items-center mr-4"
                                    :class="{ disabled: selectedRows.length <= 0 }"
                                    @click="bulkPrune"
                                >
                                    <font-awesome-icon
                                        :icon="`fas fa-seedling`"
                                        class="pruned mt-n1"
                                    />
                                    <p class="geist-font success--text bold ml-2">Prune</p>
                                </div>
                                <div
                                    class="bulk-action d-flex align-items-center"
                                    :class="{ disabled: selectedRows.length <= 0 }"
                                    @click="bulkUnprune"
                                >
                                    <font-awesome-icon
                                        :icon="`fas fa-seedling`"
                                        class="grey03--text mt-n1"
                                    />
                                    <p class="geist-font grey03--text bold ml-2">Unprune</p>
                                </div>
                            </div>
                            <div v-if="lifetimeFeesPruned && parseInt(lifetimeFeesPruned) > 0">
                                <p class="inter-font success--text d-flex align-items-center">
                                    <span v-if="selectedRows.length > 0" class="divide mr-6"></span>
                                    Lifetime fees Pruned:
                                    <span class="bold ml-2">
                                        ${{ parseInt(lifetimeFeesPruned).toLocaleString() }}
                                        <font-awesome-icon
                                            :icon="`fas fa-seedling`"
                                            class="pruned mt-n1 ml-2"
                                        />
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow-1"></div>
                    <AonButton
                        v-if="props.allowExport"
                        label="Export"
                        :loading="exportLoading"
                        :text-with-icon="true"
                        type="prominent"
                        :disabled="noRows || loading"
                        :icon-options="{ iconStyle: 'fas', iconName: 'fa-file-arrow-down' }"
                        @clicked="exportPatents()"
                    />
                </div>
                <MoatTable
                    class="w-full h-full"
                    :class="themeClass"
                    :column-defs="colDefs"
                    :tooltip-interaction="true"
                    :tooltip-mouse-track="true"
                    :tooltip-show-delay="500"
                    tooltip-show-mode="whenTruncated"
                    :side-bar="sideBar"
                    :autoSizeStrategy="sizingStrategy"
                    row-model-type="serverSide"
                    :cache-block-size="100"
                    :max-blocks-in-cache="50"
                    :get-row-id="getRowId"
                    :row-selection="rowSelection"
                    :noRowsOverlayComponent="MTCustomNoRowsOverlay"
                    :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
                    pagination
                    @grid-ready="gridInit"
                    @cell-clicked="onRowClicked"
                    @cell-key-down="onCellKeyDown"
                    @row-selected="addToBulk"
                />
                <div class="total-holder d-flex align-items-center">
                    <p class="total inter-font knightsCloak--text mr-4 pr-4">
                        Showing patent/s
                        <span class="bold">{{ firstRowOnPage.toLocaleString() }}</span>
                        <span class="bold"> - {{ lastRowOnPage.toLocaleString() }}</span> of
                        <span class="bold">{{ totalPatents.toLocaleString() }}</span> total
                    </p>
                </div>
            </div>
        </div>
    </AonModal>
</template>

<script setup>
import {
    ref,
    inject,
    onBeforeUnmount,
    onMounted,
    computed,
    watch,
    onDeactivated,
    onActivated,
    onBeforeMount,
} from 'vue'
import { usePatentViewerStore, useEntityStore, usePortfolioManagerStore } from '@/stores'
import {
    getPatentList,
    getLitPatentList,
    getPatentListIPBasics,
    getPatentListInventor,
    getConceptIntersectionPatentList,
    getConceptDirectPatentList,
} from '@/api/patentViewer.js'
import {
    submitPruneStatus,
    bulkPrune as helperBulkPrune,
    bulkUnprune as helperBulkUnprune,
    getPruningColumnDef,
} from '@/components/patentViewer/helpers/pruningHelper.js'
import { exportPatents as helperExportPatents } from '@/components/patentViewer/helpers/exportHelper.js'

import { AgGridVue as MoatTable } from '@ag-grid-community/vue3'
import { dateComparator } from '@/components/moatTable/helpers/compare.js'
import { moatParamBuilder } from '@/components/moatTable/helpers/queryBuilder.js'
import MTCustomNoRowsOverlay from '@/components/moatTable/MTCustomNoRowsOverlay.vue'

import { useI18n } from 'vue-i18n'
import { debounce } from 'lodash-es'
import { useAuth } from '@/auth/authPlugin'
import { useFlag, useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-vue'

import MoatDropdown from '@/components/helpers/moatArmory/MoatDropdown.vue'

const showQuickFilters = useFlag('ipAlpha.AAQuickFilters')
const { user } = useAuth()
const logger = inject('logger')
const filters = inject('filters')
const eventBus = inject('eventBus')
const { t } = useI18n()

const patentViewerStore = usePatentViewerStore()
const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()

const emit = defineEmits(['close-patent-list'])
const props = defineProps({
    inline: {
        type: Boolean,
        default: false,
    },
    absolute: {
        type: Boolean,
        default: false,
    },
    entityPk: {
        type: Number,
        default: null,
    },
    entityOnly: {
        type: Boolean,
        default: false,
    },
    allowPruning: {
        type: Boolean,
        default: false,
    },
    passedFilters: {
        type: Object,
        default: () => ({}),
    },
    allowExport: {
        type: Boolean,
        default: true,
    },
})

const loading = ref(true)
const exportLoading = ref(false)
const gridApi = ref(null)
const themeClass = ref('ag-theme-quartz')
const selectedQuickFilter = ref(null)
const quickFilterDropdownOptions = ref([
    {
        label: 'Strongest Patents',
        value: {
            filter: {
                legalStatus: ['Active'],
            },
            sortArray: [
                {
                    sortDir: 'desc',
                    column: 'strengthScore',
                },
            ],
        },
    },
    {
        label: 'Weakest Patents',
        value: {
            filter: {
                legalStatus: ['Active'],
            },
            sortArray: [
                {
                    sortDir: 'asc',
                    column: 'strengthScore',
                },
            ],
        },
    },
    {
        label: 'Expiring Soon',
        value: {
            filter: {
                expirationDateRange: {
                    from: new Date().toISOString().split('T')[0],
                    to: new Date(new Date().setFullYear(new Date().getFullYear() + 5))
                        .toISOString()
                        .split('T')[0],
                },
            },
            sortArray: [
                {
                    sortDir: 'asc',
                    column: 'estimatedExpirationDate',
                },
            ],
        },
    },
    {
        label: 'Litigated',
        value: {
            filter: {
                litigationCount: {
                    greaterThanNum: 0,
                },
            },
        },
    },
])
const sizingStrategy = ref({
    type: 'fitProvidedWidth',
    width: 3700,
})
const tableParams = ref({})
const noRows = ref(false)
const tablePageSize = ref(100)
const tablePageNum = ref(0)
const totalPatents = ref(0)
const patentsOnPage = ref(0)
const firstRowOnPage = ref(0)
const lastRowOnPage = ref(0)
const selectedPatentIndex = ref(0)
const sideBar = ref(null)
const previousSortModel = ref([])
const noRowsOverlayComponentParams = ref({
    message: 'There are no patents matching your search criteria.',
})
const selectedRows = ref([])
const feesPruned = ref(0)
const lifetimeFeesPruned = ref(0)

onActivated(() => {
    debounceManager.init()
    if (props.allowPruning) {
        portfolioManagerStore.allowPruning = true
    }
})

onBeforeMount(() => {
    debounceManager.init()
    if (props.allowPruning) {
        quickFilterDropdownOptions.value.push({
            label: 'Prune Eligible',
            value: {
                sortArray: [
                    {
                        sortDir: 'desc',
                        column: 'prune',
                    },
                ],
            },
        })
    }
})

onMounted(() => {
    eventBus.on('next-patent', async () => {
        const nextRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (nextRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value + 1, 'patentId')
            await processRowSelection(nextRow.data.publicationPk, nextRow.data)

            selectedPatentIndex.value++
        }
    })
    eventBus.on('previous-patent', async () => {
        const previousRow = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (previousRow?.data?.patentId) {
            gridApi.value.deselectAll()
            gridApi.value.setFocusedCell(selectedPatentIndex.value - 1, 'patentId')
            await processRowSelection(previousRow.data.publicationPk, previousRow.data)
            selectedPatentIndex.value--
        }
    })

    eventBus.on('close-patent', () => {
        patentViewerStore.targetPatentId = null
    })

    eventBus.on('prune-patent', async (patentData) => {
        if (!patentData || !patentData.publicationPk) return

        const rowNode = gridApi.value.getRowNode(patentData.patentId)

        if (rowNode) {
            const params = {
                column: { colId: 'prune' },
                data: rowNode.data,
                rowIndex: gridApi.value.getRowNode(patentData.patentId).rowIndex,
            }
            await onRowClicked(params)
        }
    })
})

onBeforeUnmount(() => {
    patentViewerStore.resetState()
    eventBus.off('next-patent')
    eventBus.off('previous-patent')
    eventBus.off('close-patent')
    debounceManager.cancelAll()
})

onDeactivated(() => {
    if (gridApi.value && selectedRows.value.length > 0) {
        gridApi.value.deselectAll()
        selectedRows.value = []
    }
    portfolioManagerStore.allowPruning = false
    debounceManager.cancelAll()
})

const colDefs = computed(() => {
    const columns = []

    if (props.allowPruning) {
        columns.push(getPruningColumnDef())
    }

    columns.push(
        {
            field: 'patentId',
            width: 140,
            cellClass: 'link',
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'title',
            flex: 2,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        }
    )

    if (props.allowPruning) {
        columns.push(
            {
                headerName: 'Next Fee Due Date',
                field: 'nextFeeDueDate',
                width: 220,
                valueGetter: (p) => {
                    if (p.data && p.data.nextFeeDueDate) {
                        let date = filters.toUTCString(p.data.nextFeeDueDate)
                        if (date === '1/1/1') {
                            date = '--'
                        }
                        return `${date}`
                    }
                    return '--'
                },
                comparator: dateComparator,
                filter: 'agDateColumnFilter',
                filterParams: {
                    suppressAndOrCondition: true,
                    buttons: ['clear'],
                    defaultOption: 'greaterThan',
                    filterOptions: ['inRange', 'greaterThan'],
                    maxNumConditions: 1,
                },
                sort: 'asc',
            },
            {
                headerName: 'Remaining Fees',
                field: 'remainingFees',
                width: 180,
                valueFormatter: (p) => {
                    if (p.data && p.data.remainingFees !== undefined) {
                        return `$${p.data.remainingFees.toLocaleString()}`
                    }
                    return '--'
                },
                filter: 'agNumberColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'equals',
                    filterOptions: ['equals', 'lessThan', 'greaterThan'],
                    maxNumConditions: 1,
                },
            },
            {
                headerName: 'Products',
                field: 'products',
                width: 150,
                flex: 2,
                filter: 'agTextColumnFilter',
                filterParams: {
                    buttons: ['clear'],
                    defaultOption: 'contains',
                    filterOptions: ['startsWith', 'equals', 'contains'],
                    maxNumConditions: 1,
                },
            }
        )
    }

    columns.push(
        {
            field: 'entities',
            headerName: 'Assignee',
            flex: 2,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'ultimateEntities',
            headerName: 'Ultimate Assignee',
            flex: 2,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'patentType',
            width: 140,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'legalStatus',
            width: 140,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['active', 'inactive'],
                valueFormatter: (p) => {
                    if (p.value) {
                        return p.value === 'active' ? 'Active' : 'Inactive'
                    }
                },
            },
        },
        {
            headerName: 'Filing Date',
            valueGetter: (p) => {
                if (p.data) {
                    let date = filters.toUTCString(p.data.filingDate)

                    if (date === '1/1/1') {
                        date = '--'
                    }

                    return `${date}`
                }
            },
            field: 'filingDate',
            comparator: dateComparator,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: {
                suppressAndOrCondition: true,
                buttons: ['clear'],
                defaultOption: 'inRange',
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Publication Date',
            valueGetter: (p) => {
                if (p.data) {
                    let date = filters.toUTCString(p.data.publicationDate)

                    if (date === '1/1/1') {
                        date = '--'
                    }

                    return `${date}`
                }
            },
            field: 'publicationDate',
            comparator: dateComparator,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: {
                suppressAndOrCondition: true,
                buttons: ['clear'],
                defaultOption: 'inRange',
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Priority Date',
            valueGetter: (p) => {
                if (p.data) {
                    let date = filters.toUTCString(p.data.priorityDate)

                    if (date === '1/1/1') {
                        date = '--'
                    }
                    return `${date}`
                }
            },
            field: 'priorityDate',
            comparator: dateComparator,
            width: 140,
            filter: 'agDateColumnFilter',
            filterParams: {
                suppressAndOrCondition: true,
                buttons: ['clear'],
                defaultOption: 'inRange',
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            headerName: 'Est. Expiration Date',
            valueGetter: (p) => {
                if (p.data) {
                    let date = filters.toUTCString(p.data.estimatedExpirationDate)

                    if (date === '1/1/1') {
                        date = '--'
                    }

                    return `${date}`
                }
            },
            field: 'estimatedExpirationDate',
            comparator: dateComparator,
            width: 150,
            filter: 'agDateColumnFilter',
            filterParams: {
                suppressAndOrCondition: true,
                buttons: ['clear'],
                defaultOption: 'inRange',
                filterOptions: ['inRange'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'applicationNumber',
            width: 150,
            filter: 'agNumberColumnFilter',
        },
        {
            field: 'claimBreadth',
            width: 150,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['Very Broad', 'Broad', 'Average', 'Narrow', 'Not Scored'],
            },
            sortingOrder: ['asc', 'desc'],
            // This tells AG Grid to use a custom sort
            comparator: () => 0,
        },
        {
            field: 'inventors',
            width: 150,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'assigneeCountry',
            width: 130,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'validity',
            width: 110,
            filter: 'agSetColumnFilter',
            filterParams: {
                values: ['Low', 'Medium', 'High', 'Not Ranked'],
            },
        },
        {
            field: 'litigationCount',
            width: 130,
            valueFormatter: (p) => {
                if (p.data) {
                    return p.data.litigationCount.toLocaleString()
                }
            },
            filter: 'agNumberColumnFilter',
        },
        {
            field: 'jurisdiction',
            width: 130,
            filter: 'agTextColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'contains',
                filterOptions: ['startsWith', 'equals', 'contains'],
                maxNumConditions: 1,
            },
        },
        {
            field: 'strengthScore',
            valueFormatter: (p) => {
                if (p.data && p.data.strengthScore !== 0) {
                    return `${p.data.strengthScore.toFixed(2)}`
                }
                return 0
            },
            width: 100,
            filter: 'agNumberColumnFilter',
            filterParams: {
                buttons: ['clear'],
                defaultOption: 'equals',
                filterOptions: ['equals', 'lessThan', 'greaterThan'],
                maxNumConditions: 1,
            },
            hide: true,
            suppressColumnsToolPanel: true,
            lockVisible: true,
        }
    )

    return columns
})

const rowSelection = computed(() => {
    if (props.allowPruning) {
        return {
            mode: 'multiRow',
            checkboxes: true,
            suppressRowDeselection: false,
            headerCheckbox: false,
        }
    } else {
        return {
            mode: 'single',
            checkboxes: false,
            enableClickSelection: true,
            suppressRowDeselection: false,
        }
    }
})

const showLitigationTitle = computed(() => patentViewerStore.listViewType === 'lit')

const showCountry = computed(() => patentViewerStore.ipBasicsObject?.country)

const showBasicsInfo = computed(() => patentViewerStore.ipBasicsObject)

const publicationType = computed(() => {
    const type = patentViewerStore.ipBasicsObject?.publicationType
    return type === 'A' ? 'Applications' : type === 'G' ? 'Grants' : 'Total'
})

const claimBreadthText = computed(() => {
    const breadth = patentViewerStore.ipBasicsObject?.claimBreadth
    return breadth ? ` | ${breadth}` : ''
})

const validityText = computed(() => {
    const validity = patentViewerStore.ipBasicsObject?.validity
    return validity ? ` - ${validity}` : ''
})

const entityTitle = computed(() => {
    const tech = patentViewerStore.technologyName
    return `${patentViewerStore.entityName}${tech ? ` - ${tech}` : ''}`
})

watch(
    () => props.passedFilters,
    (newVal, oldVal) => {
        if (newVal !== oldVal && gridApi.value) {
            applyPassedFilters(newVal)
        }
    }
)

watch(
    () => patentsOnPage.value,
    (newValue) => {
        if (gridApi.value) {
            const showResetButton = newValue === 0 && totalPatents.value !== 0
            const updatedMessage = buildNoRowsMessage(showResetButton)
            if (showResetButton) {
                setTimeout(() => {
                    gridApi.value.setGridOption('noRowsOverlayComponentParams', {
                        message: updatedMessage,
                        showResetFilterButton: showResetButton,
                        resetFilters: () => {
                            gridApi.value.setFilterModel(null)
                            gridApi.value.hideOverlay()
                            gridApi.value.refreshServerSide({ purge: true })
                        },
                    })
                    gridApi.value.showNoRowsOverlay()
                }, 100)
            } else {
                gridApi.value.hideOverlay()
            }
        }
    }
)

const gridInit = (params) => {
    gridApi.value = params.api
    setupServerSideData()
    gridApi.value.closeToolPanel()
    sideBar.value = {
        toolPanels: [
            {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: true,
                    suppressValues: true,
                    suppressPivotMode: true,
                },
            },
            {
                id: 'filters',
                labelDefault: 'Filters',
                labelKey: 'filters',
                iconKey: 'filter',
                toolPanel: 'agFiltersToolPanel',
            },
        ],
    }

    if (props.allowPruning) {
        gridApi.value.setFilterModel({
            nextFeeDueDate: {
                filterType: 'date',
                type: 'inRange',
                dateFrom: formatDate(portfolioManagerStore.dateRange[0]),
                dateTo: formatDate(portfolioManagerStore.dateRange[1]),
            },
        })
    }

    if (props.passedFilters && Object.keys(props.passedFilters).length > 0) {
        applyPassedFilters(props.passedFilters)
    }
}

const buildNoRowsMessage = (filtersApplied) => {
    const message = patentViewerStore.ipBasicsObject
        ? `There doesn't seem to be any patents for - ${patentViewerStore.entityName} - ${patentViewerStore.ipBasicsObject.country} ${
              patentViewerStore.ipBasicsObject.publicationType === 'A'
                  ? 'Applications'
                  : patentViewerStore.ipBasicsObject.publicationType === 'G'
                    ? 'Grants'
                    : 'Total'
          } ${patentViewerStore.ipBasicsObject.claimBreadth ? ' | ' + '-' + patentViewerStore.ipBasicsObject.claimBreadth : ''} ${patentViewerStore.ipBasicsObject.validity ? patentViewerStore.ipBasicsObject.validity : ''}`
        : `There are no patents matching your search criteria.`

    if (filtersApplied) {
        return message + ', with the current filters applied.'
    }

    return message
}

const getRowId = (params) => {
    return params.data.patentId
}

const setupServerSideData = async () => {
    const datasource = createServerSideDatasource()
    gridApi.value.setGridOption('serverSideDatasource', datasource)
}

const createServerSideDatasource = () => {
    let previousFilterModel = null

    return {
        getRows: (params) => {
            debounceManager.cancelAll()

            const currentFilterModel = params.request.filterModel || {}
            const filterChanged =
                JSON.stringify(previousFilterModel) !== JSON.stringify(currentFilterModel)
            previousFilterModel = JSON.parse(JSON.stringify(currentFilterModel))

            params.request.filterChanged = filterChanged

            debounceManager.dataFetch(params, async (p) => {
                const filterModel = gridApi.value.getFilterModel()
                params.request.filterModel = filterModel

                let response
                if (patentViewerStore.strategyTabPatentListObj) {
                    response = await onGetPatentList(
                        params.request,
                        patentViewerStore.strategyTabPatentListObj
                    )
                } else if (patentViewerStore.ipBasicsObject) {
                    response = await onGetPatentListIPBasics(params.request, props.entityPk)
                } else if (props.entityOnly) {
                    response = await onGetPatentListIPBasics(params.request, props.entityPk)
                    patentViewerStore.entityPkList = [props.entityPk]
                } else if (patentViewerStore.inventorPk) {
                    response = await onGetPatentListInventor(params.request)
                } else {
                    response = await onGetPatentList(params.request)
                }

                if (response && response.success) {
                    params.success({ rowData: response.rows })
                } else {
                    params.fail()
                }
            })
        },
    }
}

const onGetPatentList = async (paramsFromTable, strategyTabPatentListObj = null) => {
    const fetchFunction = async (tableParams) => {
        let nodeParams = null
        if (strategyTabPatentListObj) {
            nodeParams = strategyTabPatentListObj
        } else {
            nodeParams = {
                nodeIds: patentViewerStore.nodeIdList,
                entityPks: props.entityPk ? [props.entityPk] : patentViewerStore.entityPkList,
            }
        }

        switch (patentViewerStore.listViewType) {
            case 'lit':
                return await getLitPatentList(patentViewerStore.nodeIdList[0], tableParams)

            case 'conceptIntersection':
                return await getConceptIntersectionPatentList(
                    patentViewerStore.nodeIdList,
                    patentViewerStore.entityPkList,
                    tableParams
                )

            case 'conceptDirect':
                return getConceptDirectPatentList(
                    patentViewerStore.nodeIdList,
                    patentViewerStore.entityPkList,
                    tableParams
                )

            default:
                return await getPatentList(nodeParams, tableParams)
        }
    }

    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const onGetPatentListIPBasics = async (paramsFromTable, entityPk = null) => {
    const fetchFunction = async (tableParams) => {
        return await getPatentListIPBasics(
            entityPk ? entityPk : patentViewerStore.ipBasicsEntityPK,
            entityPk ? {} : patentViewerStore.ipBasicsObject,
            tableParams
        )
    }

    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const onGetPatentListInventor = async (paramsFromTable) => {
    const fetchFunction = async (tableParams) => {
        return await getPatentListInventor(
            {
                inventorPk: patentViewerStore.inventorPk,
                entityPk: patentViewerStore.entityPk,
            },
            tableParams
        )
    }
    return handlePatentListRequest(paramsFromTable, fetchFunction)
}

const handlePatentListRequest = async (paramsFromTable, fetchFunction) => {
    loading.value = true

    const currentSortModel = paramsFromTable.sortModel || []
    const currentFilterModel = paramsFromTable.filterModel || {}

    if (currentSortModel.some((sort) => sort.colId === 'claimBreadth')) {
        // Find the claimBreadth sort in the model
        const claimBreadthSort = currentSortModel.find((sort) => sort.colId === 'claimBreadth')

        // Add a custom parameter to indicate how to sort claimBreadth
        tableParams.value.customClaimBreadthSort = claimBreadthSort.sort
    }

    const sortChanged = JSON.stringify(previousSortModel.value) !== JSON.stringify(currentSortModel)
    const filterChanged = paramsFromTable.filterChanged || false

    selectedRows.value = []

    if (sortChanged || filterChanged) {
        tablePageNum.value = 0
        previousSortModel.value = [...currentSortModel]

        if (paramsFromTable.isFilterOrSortChangeDetection) {
            gridApi.value.refreshServerSide({ purge: true })
            return
        }
    }

    try {
        const filterModel = gridApi.value.getFilterModel()

        tableParams.value = moatParamBuilder(
            { ...paramsFromTable, filterModel },
            { page_size: tablePageSize.value, last_row_num: 0, page: tablePageNum.value },
            null
        )
        patentsOnPage.value = 0
        firstRowOnPage.value = 0
        lastRowOnPage.value = 0
        totalPatents.value = 0
        feesPruned.value = 0
        lifetimeFeesPruned.value = 0

        const data = await fetchFunction(tableParams.value)

        totalPatents.value = data.data.rowCount
        patentsOnPage.value = data.data.lastRowOnPage + 1 - data.data.firstRowOnPage
        firstRowOnPage.value = data.data.firstRowOnPage
        lastRowOnPage.value = data.data.lastRowOnPage
        feesPruned.value = data.data.feesPruned
        lifetimeFeesPruned.value = data.data.lifetimeFeesPruned

        if (data.data.results.length === 0) {
            gridApi.value.showNoRowsOverlay()
            noRows.value = true
        }
        tablePageNum.value++
        return {
            success: true,
            rows: data.data.results,
        }
    } catch (error) {
        logger.error(error)
        return {
            success: false,
        }
    } finally {
        loading.value = false
    }
}

const processRowSelection = async (patentId, rowData) => {
    if (patentViewerStore.patentExistsInCache(patentId)) {
        await updateTargetPatentId(patentId)
    } else {
        rowData.isRowData = true
        patentViewerStore.targetPatentId = patentId
        updateTargetPatentData(patentId, rowData)
    }

    eventBus.emit('patent-changed', patentId)
}

const addToBulk = (params) => {
    selectedRows.value = []
    const selectedNodes = gridApi.value.getSelectedNodes()
    if (selectedNodes && selectedNodes.length > 0) {
        selectedNodes.forEach((node) => {
            if (node.data) {
                selectedRows.value.push(node.data)
            }
        })
    }
}

const onRowClicked = async (params) => {
    if (params.column.colId === 'prune') {
        params.data.prune = !params.data.prune

        const rowNode = gridApi.value.getRowNode(params.data.patentId)
        if (rowNode) {
            rowNode.setData({ ...params.data })
        }

        params.data.prune
            ? (feesPruned.value += params.data.nextFeeDue)
            : (feesPruned.value -= params.data.nextFeeDue)

        params.data.prune
            ? (lifetimeFeesPruned.value += params.data.remainingFees)
            : (lifetimeFeesPruned.value -= params.data.remainingFees)

        if (patentViewerStore.patentDetails) {
            patentViewerStore.patentDetails.prune = params.data.prune

            selectedPatentIndex.value = params.rowIndex
            if (params?.data?.patentId) {
                await processRowSelection(params.data.publicationPk, params.data)
            }
        }

        await submitPruneStatus(params.data, [], null, logger)
    } else {
        selectedPatentIndex.value = params.rowIndex
        if (params?.data?.patentId) {
            await processRowSelection(params.data.publicationPk, params.data)
        }
    }
}

const bulkPrune = () => handleBulkPruneAction('prune')
const bulkUnprune = () => handleBulkPruneAction('unprune')

const handleBulkPruneAction = async (action) => {
    const isPruneAction = action === 'prune'
    const expectedCurrentState = !isPruneAction // false for prune, true for unprune
    const errorMessage = isPruneAction
        ? 'Some patents are already pruned. Please select only unpruned patents.'
        : 'Some patents are not pruned. Please select only pruned patents.'

    if (selectedRows.value.every((row) => row.prune === expectedCurrentState)) {
        // Calculate fee adjustments
        const feesTotalAdjustment = selectedRows.value.reduce(
            (total, row) => total + (row.nextFeeDue || 0),
            0
        )
        const lifetimeFeesTotalAdjustment = selectedRows.value.reduce(
            (total, row) => total + (row.remainingFees || 0),
            0
        )

        // Update fee totals
        feesPruned.value += isPruneAction ? feesTotalAdjustment : -feesTotalAdjustment
        lifetimeFeesPruned.value += isPruneAction
            ? lifetimeFeesTotalAdjustment
            : -lifetimeFeesTotalAdjustment

        if (patentViewerStore.patentDetails) {
            const matchingRow = selectedRows.value.find(
                (row) => row.publicationPk === patentViewerStore.patentDetails.publicationPk
            )

            // If there's a match, update the prune status in the patent details
            if (matchingRow) {
                patentViewerStore.patentDetails.prune = isPruneAction
            }
        }

        // Perform the action
        await (isPruneAction
            ? helperBulkPrune(gridApi.value, selectedRows.value, logger)
            : helperBulkUnprune(gridApi.value, selectedRows.value, logger))

        // Clear selection
        gridApi.value.deselectAll()
        selectedRows.value = []
    } else {
        eventBus.emit('snacktime', {
            type: 'caution',
            message: errorMessage,
            contextual: false,
        })
    }
}

const updateTargetPatentId = async (patentId) => {
    if (patentId) {
        patentViewerStore.targetPatentId = patentId
        await patentViewerStore.fetchPatentDetails(patentId)
    }
}

const updateTargetPatentData = (patentId, rowData) => {
    if (patentId) {
        debounceManager.patentUpdate(patentId, rowData, async (id, data) => {
            patentViewerStore.setCachedPatentRowDetail(id, data)
            await patentViewerStore.fetchPatentDetails(id)
        })
    }
}

const onCellKeyDown = async (params) => {
    selectedPatentIndex.value = params.rowIndex
    let newPatentId = null
    let row = null
    if (params.event.key === 'ArrowUp') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value - 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    } else if (params.event.key === 'ArrowDown') {
        row = gridApi.value.getDisplayedRowAtIndex(selectedPatentIndex.value + 1)
        if (row?.data?.patentId) {
            newPatentId = row.data.publicationPk
        }
    }
    await processRowSelection(newPatentId, row.data)
}

const selectQuickFilter = (models) => {
    applyPassedFilters(models.value.filter, models.value.sortArray)
}

const applyPassedFilters = (filters, sortArray) => {
    gridApi.value.setFilterModel(null)
    const filterModel = createFilterModel(filters)
    const sortModel = createSortModel(sortArray)
    applyFiltersAndRefresh(filterModel, sortModel)
}

const applyFiltersAndRefresh = (filterModel, sortModel) => {
    debounceManager.cancelAll()

    // Preserve any existing filters that should be kept
    const currentFilterModel = gridApi.value.getFilterModel() || {}
    const newFilterModel = { ...currentFilterModel, ...filterModel }

    gridApi.value.setFilterModel(newFilterModel)

    if (sortModel && sortModel.length > 0) {
        const columnState = gridApi.value.getColumnState()
        columnState.forEach((col) => {
            col.sort = null
            col.sortIndex = null
        })
        // Update sort information in column state
        sortModel.forEach((sort) => {
            const columnToSort = columnState.find((col) => col.colId === sort.colId)
            if (columnToSort) {
                columnToSort.sort = sort.sort
                columnToSort.sortIndex = 0
            }
        })

        // Apply the updated column state
        gridApi.value.applyColumnState({ state: columnState, defaultState: { sort: null } })
    }

    tablePageNum.value = 0
    refreshGrid()
}

const refreshGridWithFilters = () => {
    debounceManager.gridRefresh(() => {
        // Store the current filter model before refresh
        const currentFilterModel = gridApi.value.getFilterModel()
        // Refresh the grid
        gridApi.value.refreshServerSide({
            purge: true,
            // Pass the filter model to ensure it's preserved
            filterModel: currentFilterModel,
        })
    })
}

const refreshGrid = refreshGridWithFilters

const createFilterModel = (filters) => {
    if (!gridApi.value || !filters) return
    const filterModel = {}

    // Handle product filter (partial string match)
    if (filters.product || filters.name) {
        filterModel.products = {
            filterType: 'text',
            type: 'contains',
            filter: filters.product || filters.name,
        }
    }

    if (filters.breadth) {
        filterModel.claimBreadth = {
            filterType: 'set',
            values: [filters.breadth],
        }
    }

    if (filters.validity) {
        filterModel.validity = {
            filterType: 'set',
            values: [filters.validity],
        }
    }

    if (filters.dateRange) {
        filterModel.nextFeeDueDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: formatDate(filters.dateRange.from),
            dateTo: formatDate(filters.dateRange.to),
        }
    }

    if (filters.expirationDateRange) {
        filterModel.estimatedExpirationDate = {
            filterType: 'date',
            type: 'inRange',
            dateFrom: formatDate(filters.expirationDateRange.from),
            dateTo: formatDate(filters.expirationDateRange.to),
        }
    }

    if (filters.jurisdiction) {
        filterModel.jurisdiction = {
            filterType: 'text',
            type: 'equals',
            filter: filters.jurisdiction,
        }
    }

    if (filters.legalStatus) {
        filterModel.legalStatus = {
            filterType: 'set',
            values: filters.legalStatus,
        }
    }

    if (filters.prune) {
        filterModel.prune = {
            filterType: 'set',
            values: [filters.prune?.value.toString()],
        }
    }

    if (filters.litigationCount) {
        filterModel.litigationCount = {
            filterType: 'number',
            type: 'greaterThan',
            filter: filters.litigationCount.greaterThanNum,
        }
    }

    return filterModel
}

const createSortModel = (sortArray) => {
    if (!gridApi.value || !sortArray) return

    return sortArray.map((sort) => {
        return {
            colId: sort.column,
            sort: sort.sortDir,
        }
    })
}

const closePatentList = () => {
    emit('close-patent-list')
    patentViewerStore.resetState()
}

const exportPatents = async () => {
    exportLoading.value = true

    try {
        await helperExportPatents({
            patentViewerStore,
            entityStore,
            tableParams: tableParams.value,
            user: user.value,
            logger,
            eventBusEmit: eventBus.emit,
        })
    } catch (err) {
        logger.error(err)
    } finally {
        exportLoading.value = false
    }
}

const formatDate = (date) => {
    const newDate = new Date(date.split(' ')[0].split('T')[0] + ' 00:00:00')
    newDate.setHours(0, 0, 0, 0)
    const month = String(newDate.getMonth() + 1).padStart(2, '0')
    const day = String(newDate.getDate()).padStart(2, '0')
    const year = newDate.getFullYear()
    return `${year}-${month}-${day} 00:00:00`
}

const debounceManager = {
    debounceTime: 500,

    // Create debounced functions with references we can cancel
    dataFetch: null,
    patentUpdate: null,
    gridRefresh: null,

    // Initialize all debounced functions
    init() {
        this.dataFetch = debounce((params, callback) => callback(params), this.debounceTime)
        this.patentUpdate = debounce(
            (patentId, rowData, callback) => callback(patentId, rowData),
            this.debounceTime
        )
        this.gridRefresh = debounce((callback) => callback(), this.debounceTime)
    },

    // Cancel all pending debounced calls
    cancelAll() {
        this.dataFetch.cancel()
        this.patentUpdate.cancel()
        this.gridRefresh.cancel()
    },
}
</script>

<style lang="scss" scoped>
@forward '@/styles/new_styles.scss';

.ag-theme-quartz {
    --ag-checkbox-checked-color: #2a79d2;
    // --ag-active-color: #ffffff;
    --ag-font-family: 'Geist', sans-serif;
    --ag-font-size: 14px;

    :deep(.ag-header-cell-text) {
        font-family: 'Geist', sans-serif;
        font-weight: 800;
    }

    :deep(.ag-cell) {
        font-family: 'Geist', sans-serif;
    }

    :deep(.ag-checkbox-input-wrapper) {
        font-size: 18px !important;
    }
}

:deep(.not-pruned) path {
    fill: vars.$grey04 !important;
}

:deep(.pruned) path {
    fill: vars.$success !important;
}

.patent-list {
    &.inline {
        position: static !important;
        left: 0 !important;
        top: 0 !important;

        transform: translate(0, 0) !important;

        :deep(.a-modal-close) {
            display: none !important;
            pointer-events: none !important;
        }

        :deep(.background) {
            display: none !important;
        }

        :deep(.aon-container) {
            height: 100% !important;
        }

        :deep(.aon-row) {
            height: 100% !important;
        }

        :deep(.aon-col) {
            height: 100% !important;
        }

        :deep(.container) {
            box-shadow: none !important;
            transform: none !important;
            left: 0 !important;
            top: 0 !important;

            .patent-list-header {
                display: none !important;
            }
        }

        :deep(.aon-col-10) {
            padding: 0 !important;
        }
    }

    .bulk-action {
        padding: 10px;
        padding-bottom: 8px;
        border-radius: vars.$border-radius * 2;
        cursor: pointer;

        transition: all 0.3s ease-in-out;

        &:hover {
            background: vars.$grey07;
        }
    }

    .total-holder {
        position: absolute;
        bottom: 30px;
        left: 30px;
    }

    .fees-saved-header {
        border-left: 2px solid vars.$grey03;
    }

    &.absolute {
        position: absolute !important;
    }

    z-index: 11 !important;

    :deep(.a-modal-close) {
        background: vars.$grey04;
        transform: scale(1.2) !important;
        top: 20px;
        right: 20px;

        svg {
            color: vars.$grey02 !important;
        }
    }

    :deep(.offset-1) {
        margin-left: 0 !important;
    }

    :deep(.aon-col) {
        padding: 10px !important;
    }

    :deep(.aon-col-10) {
        flex: 0 0 100%;
        max-width: 100%;
    }

    :deep(.container) {
        width: 100% !important;
        height: 100% !important;
        padding: 0 !important;
        max-height: none !important;
        box-shadow: 0px 0px 35px 10px rgba(0, 0, 0, 0.35) !important;
    }

    :deep(.modal-content) {
        height: 100% !important;
    }

    :deep(.background) {
        width: 110%;
        height: 110%;
        top: -3px;
        left: -3px;
    }

    .patent-list-header {
        min-height: 75px;
        width: 100%;

        .source-display {
            height: 100%;
        }
    }

    .divide {
        width: 2px;
        height: 20px;
        background-color: vars.$grey04;
    }
}
</style>
